import { useInstrumentTimeSeriesData } from 'features/knowledgeDocumentViewer/hooks/useInstrumentTimeSeriesData'
import { deduplicateDatapoints } from 'features/knowledgeDocumentViewer/utils/instrumentTagUtils'
import React from 'react'
import styled from 'styled-components'

import { SparklineTooltipContent } from './SparklineTooltipContent'

interface MatchedDatapoint {
  id: string
  pi_tag: string
  name: string
  description: string
}

interface InstrumentTimeSeriesTooltipProps {
  label: string
  matchedDatapoints: MatchedDatapoint[]
  linkedDocumentCount?: number
  clickHint?: string
}

const TOOLTIP_BG = '#0f0f0f'
const TOOLTIP_BORDER = '#2a2a2a'
const TOOLTIP_SHADOW = 'rgba(0, 0, 0, 0.4)'
const LABEL_COLOR = '#ffffff'

const TooltipContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 15rem;
  max-width: 20rem;
  background: ${TOOLTIP_BG};
  border-radius: 0.625rem;
  padding: 0.75rem;
  border: 1px solid ${TOOLTIP_BORDER};
  box-shadow: 0 0.5rem 2rem ${TOOLTIP_SHADOW};
`

const TooltipHeader = styled.div`
  display: flex;
  flex-direction: column;
`

const TooltipLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${LABEL_COLOR};
  letter-spacing: -0.01em;
`

export function InstrumentTimeSeriesTooltip({
  label,
  matchedDatapoints,
  linkedDocumentCount = 0,
  clickHint = 'dashboard',
}: InstrumentTimeSeriesTooltipProps) {
  const dedupedDatapoints = deduplicateDatapoints(matchedDatapoints)
  const datapointIds = dedupedDatapoints.slice(0, 3).map((dp) => dp.id)

  const { sparklineData, isLoading, error } = useInstrumentTimeSeriesData(
    datapointIds,
    datapointIds.length > 0
  )

  const hasTimeSeriesData =
    !isLoading &&
    !error &&
    sparklineData.length > 0 &&
    dedupedDatapoints.length > 0
  const hasLinkedDocuments = linkedDocumentCount > 0

  const actionsHint =
    hasTimeSeriesData || hasLinkedDocuments ? (
      <>
        {hasTimeSeriesData && `Click: ${clickHint}`}
        {hasTimeSeriesData && hasLinkedDocuments && ' · '}
        {hasLinkedDocuments &&
          `Shift+click: ${linkedDocumentCount} doc${linkedDocumentCount !== 1 ? 's' : ''}`}
      </>
    ) : undefined

  return (
    <TooltipContainer>
      <TooltipHeader>
        <TooltipLabel>{label}</TooltipLabel>
      </TooltipHeader>

      <SparklineTooltipContent
        sparklineData={sparklineData}
        isLoading={isLoading}
        error={!!error}
        datapointCount={dedupedDatapoints.length}
        animateLoading
        actionsHint={actionsHint}
      />
    </TooltipContainer>
  )
}
